import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  encodeSearchWord,
  firstDayOfMonth,
  getCurrentStartDate,
  getDateList,
  lastDayOfMonth,
  timestamp2Localestring,
} from '../../common/helper';
import {
  KEY_BOOK_GROUP_LIST,
  KEY_BOOK_INFO_LIST,
  KEY_CLASS_READ_RANK_LIST,
  KEY_CLASS_WALK_RANK_LIST,
  KEY_CLASS_WRITE_RANK_LIST,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwClassStatTab } from '../commonComponent/SwClassStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { ClassRankTable } from './ClassRankTable';
import { SwSelectMonth } from '../commonComponent/SwSelectMonth';
import {
  downloadClassRankList,
  getClassReadRankList,
  getClassWalkRankList,
  getClassWriteRankList,
} from '../../common/api/ApiClassStat';
import { selectSchoolInfoState } from '../../common/atom';

interface Props {}

export const ClassRank: React.FC<Props> = () => {
  const { loginVal } = useSession();

  const [schoolClassInfo, setSchoolClassInfo] = useRecoilState(
    selectSchoolInfoState
  );
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestReadData, setRequestReadData] = useState<any>();
  const [requestWalkData, setRequestWalkData] = useState<any>();
  const [requestWriteData, setRequestWriteData] = useState<any>();
  const [loading, setLoading] = useState<any>(false);
  const [targetMonth, setTargetMonth] = useState<any>(getCurrentStartDate());
  const [targetDay, setTargetDay] = useState<any>(0);

  const rowsPerPageOptions = [5, 10, 15, 20];
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchKey, setSearchKey] = useState<string>('book-group');
  const [searchWord, setSearchWord] = useState<string>('');
  const [dialogMessage, setDialogMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openUserStat, setOpenUserStat] = useState<boolean>(false);

  const getStartDate = () => {
    if (targetDay === 0) {
      return firstDayOfMonth(targetMonth * 1000).getTime();
    }
    const month = new Date(targetMonth * 1000).getMonth();
    const date = new Date(loginVal.value.user.year, month, targetDay);
    return date.getTime();
  };
  const getEndDate = () => {
    if (targetDay === 0) {
      return lastDayOfMonth(targetMonth * 1000).getTime();
    }
    const month = new Date(targetMonth * 1000).getMonth();
    const date = new Date(loginVal.value.user.year, month, targetDay);
    return date.getTime() + (24 * 60 * 60 - 1) * 1000;
  };

  const {
    data,
    isLoading,
    refetch: refetchClassReadRank,
  } = useQuery(
    KEY_CLASS_READ_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          startDate: getStartDate(),
          endDate: getEndDate(),
        };
        return getClassReadRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestReadData(res);
        } else {
          setRequestReadData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassWalkRank } = useQuery(
    KEY_CLASS_WALK_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          startDate: getStartDate(),
          endDate: getEndDate(),
        };
        return getClassWalkRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWalkData(res);
        } else {
          setRequestWalkData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassWriteRank } = useQuery(
    KEY_CLASS_WRITE_RANK_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          page: 0,
          rowsPerPage: 0,
          searchKey: searchKey,
          searchWord: encodeSearchWord(searchKey, searchWord),
          startDate: getStartDate(),
          endDate: getEndDate(),
        };
        return getClassWriteRankList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestWriteData(res);
        } else {
          setRequestWriteData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const loadClassStat = () => {
    refetchClassReadRank();
    refetchClassWalkRank();
    refetchClassWriteRank();
  };

  const downloadClassRank = (newType: string, orderType: number) => {
    const newData: any = {
      type:
        newType === KEY_READ_TEXT
          ? 'read'
          : newType === KEY_WALK_TEXT
          ? 'walk'
          : 'write',
      orderType: orderType,
      schoolSeq: schoolClassInfo.schoolSeq,
      schoolGrade: schoolClassInfo.schoolGrade,
      schoolClass: schoolClassInfo.schoolClass,
      page: 0,
      rowsPerPage: 0,
      searchKey: searchKey,
      searchWord: encodeSearchWord(searchKey, searchWord),
      startDate: getStartDate(),
      endDate: getEndDate(),
    };
    downloadClassRankList(newData);
  };

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      loadClassStat();
    }
  }, [targetMonth, targetDay]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      loadClassStat();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      loadClassStat();
    }
  }, []);

  return (
    <>
      <Paper sx={{ p: '1.5rem' }}>
        <SwClassStatTab
          tabValue={1}
          schoolClassInfo={schoolClassInfo}
          setSchoolClassInfo={setSchoolClassInfo}
        />
        <SwSelectMonth
          onChangeMonth={item => {
            setTargetMonth(item);
          }}
          onChangeDay={item => setTargetDay(item)}
          showSelectDay={false}
        />
        <ClassRankTable
          title={KEY_READ_TEXT}
          targetMonth={targetMonth}
          data={requestReadData}
          onDownload={orderType => downloadClassRank(KEY_READ_TEXT, orderType)}
        />
        <Divider sx={{ m: '', p: '' }} />
        <ClassRankTable
          title={KEY_WALK_TEXT}
          targetMonth={targetMonth}
          data={requestWalkData}
          onDownload={orderType => downloadClassRank(KEY_WALK_TEXT, orderType)}
        />
        <Divider sx={{ m: '', p: '' }} />
        <ClassRankTable
          title={KEY_WRITE_TEXT}
          targetMonth={targetMonth}
          data={requestWriteData}
          onDownload={orderType => downloadClassRank(KEY_WRITE_TEXT, orderType)}
        />
      </Paper>
      <SwSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        contents={dialogMessage}
      />
      <SwUserStatDetail
        memberSeq={1}
        open={openUserStat}
        onClose={() => setOpenUserStat(false)}
      />
      {loading && <SwLoading />}
    </>
  );
};
